'use client';

import React, { useState } from 'react';
import { ArrowUp, ArrowDown, Copy, Trash2, Plus, Lock, GripVertical, RotateCcw } from 'lucide-react';
import type { PageSection } from '@/lib/storefront/section-library';
import { getSectionComponent, getVariation } from '@/lib/storefront/section-library';
import type { SectionCategory, SectionStyle } from '@/lib/storefront/section-templates';
import { resolveSectionStyle } from '@/lib/storefront/section-templates';
import { PreviewFrame, type PreviewDevice, type PreviewZoom } from './PreviewFrame';

const WIDTH_ORDER: SectionStyle['containerWidth'][] = ['narrow', 'normal', 'wide', 'full'];

interface SectionCanvasProps {
  sections: PageSection[];
  selectedId: string | null;
  previewMode: boolean;
  deviceWidth: PreviewDevice;
  zoom: PreviewZoom;
  pageStyle?: React.CSSProperties;
  lockedCategories?: SectionCategory[];
  onSelect: (id: string) => void;
  onMove: (id: string, direction: -1 | 1) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onDuplicate: (id: string) => void;
  onReset: (id: string) => void;
  onRemove: (id: string) => void;
  onAddAt: (index: number) => void;
  onChangeStyle?: (id: string, style: SectionStyle) => void;
}

export function SectionCanvas({
  sections,
  selectedId,
  previewMode,
  deviceWidth,
  zoom,
  pageStyle,
  lockedCategories = [],
  onSelect,
  onMove,
  onReorder,
  onDuplicate,
  onReset,
  onRemove,
  onAddAt,
  onChangeStyle,
}: SectionCanvasProps) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const nudgeWidth = (section: PageSection, direction: -1 | 1) => {
    if (!onChangeStyle) return;
    const current = section.style.containerWidth ?? 'normal';
    const index = WIDTH_ORDER.indexOf(current);
    const next = WIDTH_ORDER[Math.min(WIDTH_ORDER.length - 1, Math.max(0, index + direction))];
    if (next && next !== current) {
      onChangeStyle(section.id, { ...section.style, containerWidth: next });
    }
  };

  return (
    <PreviewFrame device={deviceWidth} zoom={zoom}>
      <div className="min-h-[480px] bg-white text-slate-900" style={pageStyle}>
        {sections.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-3 px-6 py-24 text-center">
            <p className="text-sm font-semibold text-slate-700">This page is empty</p>
            <p className="max-w-sm text-sm text-slate-500">
              Add a header, hero or any other section from the library to start building.
            </p>
            <button
              type="button"
              onClick={() => onAddAt(0)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold text-white hover:bg-slate-700"
            >
              <Plus className="h-3.5 w-3.5" /> Add the first section
            </button>
          </div>
        )}

        {sections.map((section, index) => {
          const Component = getSectionComponent(section.category, section.variationId);
          const variation = getVariation(section.category, section.variationId);
          const isLocked = lockedCategories.includes(section.category);
          const isSelected = !previewMode && !isLocked && section.id === selectedId;
          const isDropTarget = overIndex === index && dragIndex !== null && dragIndex !== index;

          return (
            <div
              key={section.id}
              className={`relative ${isDropTarget ? 'ring-2 ring-inset ring-orange-500' : ''} ${
                dragIndex === index ? 'opacity-60' : ''
              }`}
              onDragOver={(event) => {
                if (previewMode || isLocked || dragIndex === null) return;
                event.preventDefault();
                setOverIndex(index);
              }}
              onDrop={(event) => {
                event.preventDefault();
                const from = dragIndex ?? Number(event.dataTransfer.getData('text/plain'));
                if (Number.isFinite(from) && from !== index) onReorder(from, index);
                setDragIndex(null);
                setOverIndex(null);
              }}
              onDragEnd={() => {
                setDragIndex(null);
                setOverIndex(null);
              }}
            >
              {!previewMode && !isLocked && (
                <div className="group/add absolute inset-x-0 -top-3 z-20 flex justify-center">
                  <button
                    type="button"
                    onClick={() => onAddAt(index)}
                    className="rounded-full border border-slate-300 bg-white px-2 py-1 text-[10px] font-semibold text-slate-600 opacity-0 shadow-sm transition-opacity group-hover/add:opacity-100"
                    title="Insert a section before this one"
                  >
                    + Insert before
                  </button>
                </div>
              )}

              <div
                role={previewMode || isLocked ? undefined : 'button'}
                tabIndex={previewMode || isLocked ? undefined : 0}
                onClickCapture={(event) => {
                  const target = event.target as HTMLElement;
                  if (target.closest('[data-section-toolbar]')) return;
                  if (target.closest('[data-width-handle]')) return;
                  if (target.closest('a, button, input, select, textarea, form')) event.preventDefault();
                  if (!previewMode && !isLocked) {
                    event.stopPropagation();
                    onSelect(section.id);
                  }
                }}
                onKeyDown={
                  previewMode || isLocked
                    ? undefined
                    : (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onSelect(section.id);
                        }
                      }
                }
                className={`relative outline-none transition-shadow ${
                  isSelected
                    ? 'outline outline-2 -outline-offset-2 outline-orange-500'
                    : !previewMode && !isLocked
                      ? 'cursor-pointer hover:outline hover:outline-2 hover:-outline-offset-2 hover:outline-orange-300'
                      : ''
                }`}
              >
                {!previewMode && (
                  <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex items-start justify-between p-2">
                    <span
                      draggable={!isLocked}
                      onDragStart={(event) => {
                        if (isLocked) return;
                        setDragIndex(index);
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', String(index));
                      }}
                      className={`pointer-events-auto inline-flex cursor-grab items-center gap-1 rounded-md px-2 py-1 text-[10px] font-semibold shadow-sm active:cursor-grabbing ${
                        isLocked
                          ? 'bg-sky-600/90 text-white'
                          : isSelected
                            ? 'bg-orange-500 text-white'
                            : 'bg-slate-900/80 text-white'
                      }`}
                      title={isLocked ? 'Site-wide section' : 'Drag to reorder'}
                    >
                      {isLocked ? (
                        <>
                          <Lock className="h-3 w-3" /> Site-wide · edit on Home
                        </>
                      ) : (
                        <>
                          <GripVertical className="h-3 w-3" />
                          {variation?.name ?? section.category}
                        </>
                      )}
                    </span>

                    {!isLocked && (
                      <div
                        data-section-toolbar
                        className={`pointer-events-auto flex items-center gap-0.5 rounded-lg border border-slate-200 bg-white p-0.5 shadow ${
                          isSelected ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onMove(section.id, -1);
                          }}
                          disabled={index === 0}
                          className="rounded p-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-30"
                          title="Move up"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onMove(section.id, 1);
                          }}
                          disabled={index === sections.length - 1}
                          className="rounded p-1.5 text-slate-600 hover:bg-slate-100 disabled:opacity-30"
                          title="Move down"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onDuplicate(section.id);
                          }}
                          className="rounded p-1.5 text-slate-600 hover:bg-slate-100"
                          title="Duplicate"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onReset(section.id);
                          }}
                          className="rounded p-1.5 text-slate-600 hover:bg-slate-100"
                          title="Reset section to defaults"
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            onRemove(section.id);
                          }}
                          className="rounded p-1.5 text-rose-600 hover:bg-rose-50"
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {isSelected && onChangeStyle && (
                  <>
                    <button
                      type="button"
                      data-width-handle
                      onClick={(event) => {
                        event.stopPropagation();
                        nudgeWidth(section, -1);
                      }}
                      className="absolute left-1 top-1/2 z-10 -translate-y-1/2 rounded-md border border-slate-300 bg-white px-1 py-3 text-[9px] font-bold text-slate-600 shadow hover:bg-slate-50"
                      title={`Narrower (${section.style.containerWidth})`}
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      data-width-handle
                      onClick={(event) => {
                        event.stopPropagation();
                        nudgeWidth(section, 1);
                      }}
                      className="absolute right-1 top-1/2 z-10 -translate-y-1/2 rounded-md border border-slate-300 bg-white px-1 py-3 text-[9px] font-bold text-slate-600 shadow hover:bg-slate-50"
                      title={`Wider (${section.style.containerWidth})`}
                    >
                      ›
                    </button>
                  </>
                )}

                {Component ? (
                  <Component
                    content={section.content}
                    style={resolveSectionStyle(section.style, deviceWidth)}
                  />
                ) : (
                  <div className="px-6 py-10 text-center text-sm text-rose-600">
                    Unknown section “{section.category}/{section.variationId}”.
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {!previewMode && sections.length > 0 && (
          <div className="border-t border-dashed border-slate-200 bg-slate-50 px-4 py-4">
            <button
              type="button"
              onClick={() => onAddAt(sections.length)}
              className="w-full rounded-xl border border-dashed border-slate-300 bg-white py-3 text-xs font-semibold text-slate-600 hover:border-slate-400"
            >
              + Add a section after the last one
            </button>
          </div>
        )}
      </div>
    </PreviewFrame>
  );
}
